import { useCallback } from 'react';
import { useDiffitUserContext } from '../contexts/DiffitUserContext';
import { buildShareUrl } from '../utils/shareUrl';

/**
 * The signed-in user's referral code, or undefined when signed out.
 *
 * The code is minted server-side for every signed-in user (see _async_ensure_referral_code in
 * diffit-api's user router) and rides along on the /user response, independent of whether
 * they're enrolled in any referral program.
 *
 * Prefer useShareUrl below. Reach for this only when a surface wants the referral code
 * *without* the copy-link utm params — the social share buttons, which shouldn't be reported as
 * copied links.
 *
 * @returns {string|undefined}
 */
export function useReferralCode() {
  const { diffitUser } = useDiffitUserContext();
  return diffitUser?.referral_code;
}

/**
 * Returns a builder for outbound share links, pre-bound to the signed-in user's referral code.
 *
 * A hook rather than each surface reading `diffitUser` for itself: "the referral code comes from
 * the user context" stays one fact in one place, and the next copy-a-link surface picks the code
 * up by construction instead of by someone remembering to. buildShareUrl leaves the URL alone
 * when there's no code, so signed-out callers need no branch.
 *
 * @returns {(url: string, options?: {source?: string, medium?: string, linkSource?: string}) => string}
 */
export function useShareUrl() {
  const referralCode = useReferralCode();

  return useCallback(
    // referralCode last so a caller's options can't accidentally drop it.
    (url, options = {}) => buildShareUrl(url, { ...options, referralCode }),
    [referralCode]
  );
}
